import { ChangeDetectionStrategy, Component, HostBinding, Inject, TemplateRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { TypedFormGroup } from '@feature/forms/form-model';

type Color = 'primary' | 'accent' | 'warn';

type Placement = 'prefix' | 'suffix';

type Button = {
  color?: Color;
  /** Accepts `mat-icon` */
  icon?: string;
  iconPlacement?: Placement;
  label: string;
  type?: 'basic' | 'flat' | 'raised' | 'stroked';
};
type Checkbox = {
  label: string;
  type?: 'enable';
  checked?: boolean;
  show?: boolean;
  required?: boolean;
};

export type BulletedListItem = {
  icon?: string;
  text: string;
};

export type TitleIcon = {
  name: string;
  type?: string;
};

export type DialogData<T extends unknown = unknown, U extends unknown = unknown> = {
  bulletedList?: BulletedListItem[];
  confirmButton?: Button;
  confirmButtonDisabled?: boolean;
  confirmHandler?: () => void;
  checkConfirmButtons?: Checkbox[];
  dismissButton: Button;
  dismissButtonDisabled?: boolean;
  dismissHandler?: () => void;

  extraButton?: Button;
  extraButtonDisabled?: boolean;
  extraHandler?: () => void;
  isLoading?: () => Observable<boolean>;
  noContentPadding?: boolean;
  shouldSeparateFooter?: boolean;
  shouldStretchFooterButtons?: boolean;
  templateRef?: TemplateRef<unknown>;
  templateRefContext?: U;
  text?: string;
  title?: string;
  titleIcon?: TitleIcon;
  titleTemplateRef?: TemplateRef<unknown>;
  titleTemplateRefContext?: T;
};

// export type DialogResult = boolean;
export type DialogResult = 'stay' | 'leave-without-saving' | 'leave-and-save' | boolean;

@Component({
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DialogComponent {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @HostBinding('class.wai-dialog') public className = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
    private dialogRef: MatDialogRef<DialogComponent, DialogResult>,
    private router: Router,
    private sanitizer: DomSanitizer,
  ) {
    const confirmButton = this.dialogData.confirmButton
      ? {
          ...{ icon: 'check', color: 'primary', iconPlacement: 'prefix', type: 'basic' },
          ...dialogData.confirmButton,
        }
      : null;

    const extraButton = this.dialogData.extraButton
      ? {
          ...{ icon: 'save', color: 'accent', iconPlacement: 'prefix', type: 'basic' },
          ...dialogData.extraButton,
        }
      : null;

    Object.assign(dialogData, {
      confirmButton,
      extraButton,
      dismissButton: {
        ...{ icon: 'block', iconPlacement: 'prefix', type: 'basic' },
        ...dialogData.dismissButton,
      },
      text: dialogData.text,
      title: dialogData.title,
    });

    this.router.events
      .pipe(
        filter((event) => {
          return event instanceof NavigationEnd;
        }),
        tap(() => this.dialogRef.close()),
        take(1),
      )
      .subscribe();
  }

  // public onConfirm(): void {
  //   if (this.dialogData.confirmHandler) {
  //     this.dialogData.confirmHandler();
  //   } else {
  //     this.dialogRef.close(true);
  //   }
  // }

  // public onDismiss(): void {
  //   if (this.dialogData.dismissHandler) {
  //     this.dialogData.dismissHandler();
  //   } else {
  //     this.dialogRef.close(false);
  //   }
  // }

  public onDismiss(): void {
    if (this.dialogData.dismissHandler) {
      this.dialogData.dismissHandler();
    } else {
      this.dialogRef.close('stay');
      this.dialogRef.close(false);
    }
  }

  public onConfirm(): void {
    if (this.dialogData.confirmHandler) {
      this.dialogData.confirmHandler();
    } else {
      this.dialogRef.close('leave-without-saving');
    }
  }

  public onExtraHandle(): void {
    if (this.dialogData.extraHandler) {
      this.dialogData.extraHandler();
    } else {
      this.dialogRef.close('leave-and-save');
    }
  }

  public checkConfirmButtonChange(event, button): void {
    button.checked = event;
    this.updateConfirmButtonState();
  }
  private updateConfirmButtonState(): void {
    this.dialogData.confirmButtonDisabled = this.dialogData.checkConfirmButtons
      .filter((button) => button.required)
      .some((button) => !button.checked);
  }

  public get sanitizedText(): SafeHtml | null {
    if (!this.dialogData?.text) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.dialogData.text);
  }

  public sanitizeHtml(html: string | null | undefined): SafeHtml | null {
    if (!html) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
